import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowRight, Shield, KeyRound } from "lucide-react";

export default function LoginPage() {
    const { login, lang, setLang } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [show2fa, setShow2fa] = useState(false);
    const [totpCode, setTotpCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [backupCode, setBackupCode] = useState("");
    const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem("mytacs_splash_shown"));

    useEffect(() => {
        if (!showSplash) return;
        sessionStorage.setItem("mytacs_splash_shown", "1");
        const t1 = setTimeout(() => setShowSplash(false), 1800);
        return () => clearTimeout(t1);
    }, [showSplash]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(
                email,
                password,
                show2fa && !useBackup ? totpCode : undefined,
                show2fa && useBackup ? backupCode : undefined,
            );
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail || "Login failed";
            if (status === 403 && /2FA/i.test(detail)) {
                // Server says 2FA code is required → switch to 2FA challenge mode
                setShow2fa(true);
                setError("");
            } else {
                setError(detail);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen auth-backdrop flex" data-testid="login-page">
            {/* Splash overlay */}
            {showSplash && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white splash-fade-out" data-testid="login-splash">
                    <div className="relative flex flex-col items-center">
                        {/* Pulsing glow */}
                        <div className="absolute -inset-20 rounded-full bg-neutral-900/15 blur-3xl animate-pulse" />
                        <div className="absolute -inset-10 rounded-full bg-neutral-900/10 blur-2xl animate-pulse" style={{ animationDelay: "0.3s" }} />
                        {/* DuTACS logo on dark container */}
                        <div className="relative w-64 h-24 rounded-2xl bg-black flex items-center justify-center px-6 shadow-2xl shadow-black/30 splash-logo-pop">
                            <img
                                src="/brand/dutacs-logo-400.png"
                                alt="DuTACS Travel Business Solutions"
                                className="max-h-16 w-auto"
                                draggable="false"
                            />
                        </div>
                        {/* Wordmark */}
                        <div className="relative mt-6 font-display text-3xl font-bold text-neutral-900 tracking-tight splash-text-rise">MyTACS</div>
                        <div className="relative mt-2 text-xs font-semibold uppercase tracking-[0.3em] text-neutral-500 splash-text-rise" style={{ animationDelay: "0.2s" }}>
                            {lang === "tr" ? "Ekip · Görev · Akış" : lang === "de" ? "Team · Aufgabe · Fluss" : "Team · Task · Flow"}
                        </div>
                    </div>
                </div>
            )}
            {/* Left: hero */}
            <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 border-r border-neutral-200 relative overflow-hidden">
                <div className="bg-grid absolute inset-0 opacity-50 pointer-events-none" />
                <div className="relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="h-10 px-3 rounded-md bg-black flex items-center justify-center">
                            <img
                                src="/brand/dutacs-logo-200.png"
                                alt="DuTACS"
                                className="h-6 w-auto"
                                draggable="false"
                            />
                        </div>
                        <span className="font-display font-bold text-xl tracking-tight text-neutral-900">MyTACS</span>
                    </div>
                </div>

                <div className="relative z-10 max-w-md">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-4">
                        {lang === "tr" ? "Ekip · Görev · Akış" : lang === "de" ? "Team · Aufgabe · Fluss" : "Team · Task · Flow"}
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05] text-neutral-900">
                        {lang === "tr" ? "Şirketinizin görev akışı, tek bir merkezde." : lang === "de" ? "Der Aufgabenfluss Ihres Unternehmens, an einem Ort." : "Your company's task flow, in one place."}
                    </h1>
                    <p className="mt-5 text-neutral-600 text-base leading-relaxed">
                        {lang === "tr"
                            ? "Departmanlar, alt görevler, ortak takvim ve canlı bildirimlerle ekibinizin tüm operasyonunu kolayca yönetin."
                            : lang === "de"
                            ? "Abteilungen, Unteraufgaben, gemeinsamer Kalender und Live-Benachrichtigungen — alles für Ihr Team."
                            : "Spaces, subtasks, a shared calendar and real-time notifications — all in one tool for your team."}
                    </p>
                </div>

                <div className="relative z-10 text-xs text-neutral-500">
                    © {new Date().getFullYear()} DuTACS GmbH · {lang === "tr" ? "Şirket içi kullanım için" : lang === "de" ? "Für die interne Unternehmensnutzung" : "For internal company use"}
                </div>
            </div>

            {/* Right: form */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-sm">
                    <div className="flex items-center justify-between mb-10">
                        <div className="flex items-center gap-2.5 lg:hidden">
                            <div className="h-8 px-2 rounded-md bg-black flex items-center justify-center">
                                <img
                                    src="/brand/dutacs-logo-200.png"
                                    alt="DuTACS"
                                    className="h-5 w-auto"
                                    draggable="false"
                                />
                            </div>
                            <span className="font-display font-bold tracking-tight">MyTACS</span>
                        </div>
                        <div className="ml-auto flex items-center gap-1 text-xs">
                            {["tr", "en", "de"].map((l) => (
                                <button
                                    key={l}
                                    data-testid={`lang-${l}-btn`}
                                    onClick={() => setLang(l)}
                                    className={`px-2 py-1 rounded ${lang === l ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
                                >{l.toUpperCase()}</button>
                            ))}
                        </div>
                    </div>

                    <h2 className="font-display text-3xl font-bold tracking-tight">{t(lang, "loginTitle")}</h2>
                    <p className="mt-2 text-sm text-neutral-600">{t(lang, "loginSubtitle")}</p>

                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                        {!show2fa && (
                            <>
                                <div>
                                    <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">
                                        {t(lang, "email")}
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        required
                                        data-testid="login-email-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="name@dutacs.de"
                                        className="mt-1.5 h-11"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">
                                        {t(lang, "password")}
                                    </Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        required
                                        data-testid="login-password-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="mt-1.5 h-11"
                                    />
                                </div>
                            </>
                        )}

                        {show2fa && (
                            <div className="space-y-3" data-testid="login-2fa-step">
                                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-2.5">
                                    <Shield className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                                    <div className="text-xs">
                                        <p className="font-semibold text-emerald-900">{lang === "de" ? "Zwei-Faktor-Authentifizierung" : "Two-factor authentication"}</p>
                                        <p className="text-emerald-800 mt-0.5">{lang === "de" ? "Öffne deine Authenticator-App und gib den 6-stelligen Code ein." : "Open your Authenticator app and enter the 6-digit code."}</p>
                                    </div>
                                </div>
                                {!useBackup ? (
                                    <div>
                                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">
                                            {lang === "de" ? "6-stelliger Code" : "6-digit code"}
                                        </Label>
                                        <Input
                                            autoFocus
                                            inputMode="numeric"
                                            pattern="\d{6}"
                                            maxLength={6}
                                            value={totpCode}
                                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                            placeholder="123456"
                                            className="mt-1.5 h-14 text-2xl text-center tracking-widest font-mono"
                                            data-testid="login-totp-input"
                                        />
                                        <button type="button" onClick={() => setUseBackup(true)} className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="login-use-backup">
                                            <KeyRound className="w-3 h-3" />
                                            {lang === "de" ? "Authenticator nicht verfügbar? Backup-Code verwenden" : "Authenticator unavailable? Use backup code"}
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-neutral-700">
                                            {lang === "de" ? "Backup-Code (XXXX-XXXX)" : "Backup code (XXXX-XXXX)"}
                                        </Label>
                                        <Input
                                            autoFocus
                                            value={backupCode}
                                            onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                                            placeholder="ABCD-1234"
                                            className="mt-1.5 h-11 font-mono uppercase"
                                            data-testid="login-backup-input"
                                        />
                                        <button type="button" onClick={() => setUseBackup(false)} className="mt-2 text-xs text-neutral-500 hover:underline">
                                            ← {lang === "de" ? "Zurück zum 6-stelligen Code" : "Back to 6-digit code"}
                                        </button>
                                    </div>
                                )}
                                <button
                                    type="button"
                                    onClick={() => { setShow2fa(false); setTotpCode(""); setBackupCode(""); setUseBackup(false); }}
                                    className="text-xs text-neutral-500 hover:underline"
                                    data-testid="login-cancel-2fa"
                                >
                                    ← {lang === "de" ? "Anderes Konto verwenden" : "Use different account"}
                                </button>
                            </div>
                        )}

                        {error && (
                            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={busy || (show2fa && !useBackup && totpCode.length !== 6) || (show2fa && useBackup && !backupCode)}
                            data-testid="login-submit-btn"
                            className="w-full h-11 bg-neutral-900 hover:bg-neutral-800 text-white"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                <>
                                    {show2fa ? (lang === "de" ? "Verifizieren" : "Verify") : t(lang, "login")}
                                    <ArrowRight className="w-4 h-4 ml-1" />
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 flex items-center justify-center gap-4 text-xs text-neutral-500" data-testid="login-legal-footer">
                        <a href="/impressum" className="hover:text-neutral-800 hover:underline" data-testid="login-imprint-link">Impressum</a>
                        <span aria-hidden="true">·</span>
                        <a href="/datenschutz" className="hover:text-neutral-800 hover:underline" data-testid="login-privacy-link">Datenschutz</a>
                    </div>
                </div>
            </div>
        </div>
    );
}
